// Kern der App ohne Oberfläche: Inhalt, lokaler Lernstand, Konto und Synchronisierung. Die Oberfläche (ui.js) liest core.S und ruft die Aktionen.
// Testbar ohne Browser (test/*.test.js): Speicher, Supabase-Client und Uhr werden hineingereicht.
import { LocalDeck, adoptUserData, decksOf, removeUserData, PILOTS } from './local-deck.js';
import { deriveC1, policyOf, localTimezone, studyDay, previewDue } from './learning-policy.js';
import { schedulerFor, derive, append, withLocal, rateUnit, DAY } from './learning.js';
import { convertPrototype, protoKeyToDeck, PROTO_PREFIX } from './migrate.js';
import { deviceId as getDeviceId, randomId } from './ids.js';
import { readJson } from './storage.js';
import { SyncService } from './sync-service.js';
import { startPosOf, checkFromOf } from './placement.js';
import { deriveSimple, FLOW } from './simple-learning.js';

export const LOCAL_USER = 'lokal';

export function deckIdOf(content) {
  const m = content.meta || {};
  return m.deckId || (m.deck && m.deck.startsWith(`${m.language}-`) ? m.deck : `${m.language || 'xx'}-${m.deck || 'demo'}`);
}

export function createCore({ config, content, store, now: clock = () => Date.now() }) {
  const deckId = deckIdOf(content);
  const device = getDeviceId(store);
  const listeners = new Set();
  const emit = what => listeners.forEach(fn => { try { fn(what); } catch (e) { console.error(e); } });

  const core = {
    config, content, deckId, deviceId: device, store,
    userKey: LOCAL_USER, local: null, D: null,
    // Live-Objekt für die Oberfläche (Felder werden ersetzt, das Objekt bleibt dasselbe)
    S: { units: {}, seen: {}, days: {}, log: [], hidden: { s: {}, u: {} }, profile: {}, settings: {}, reports: [], collection: {}, shiftDays: 0 },
    timeTravel: !!(config.dev && !config.online),
    on(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    emit,
  };

  const sch = () => schedulerFor(core.local.user.settings.retention);
  const quietSet = () => new Set(core.local.state.quiet);
  core.now = () => clock() + (core.timeTravel ? (core.local.state.shiftDays || 0) * DAY : 0);

  // ---------- Kartenablauf C1 (Entscheidung 21): eigenes Protokoll, Lernstand daraus abgeleitet ----------
  core.tz = localTimezone();
  let C1cache = null, C1dirty = true;   // Lernstand C1 erst beim Lesen ableiten (nicht nach jedem einzelnen Ereignis)
  Object.defineProperty(core, 'C1', { get() { if (C1dirty) { const L = core.local; C1cache = L && L.v2 ? deriveC1(L.v2.log, content, sch(), policyOf(L.state.pilot)) : null; C1dirty = false; } return C1cache; } });
  core.pilot = () => (core.local && PILOTS.includes(core.local.state.pilot) ? core.local.state.pilot : null);
  core.policy = () => policyOf(core.pilot());
  const deriveC1Now = () => { C1dirty = true; };
  // Pilot für diese Einschreibung festlegen. Nie still wechseln: nur, wenn noch keiner gesetzt ist; mit force (Entwicklung) auch danach.
  core.setPilot = (p, { force = false } = {}) => {
    const L = core.local;
    if (p !== null && !PILOTS.includes(p)) throw new Error('unbekannter Pilot ' + p);
    if (L.state.pilot === p || (L.state.pilot && !force)) return false;
    L.state.pilot = p; L.state.profileDirty = true; L.save();
    core.rederive(); emit('changed');
    return true;
  };
  // Ein C1-Ereignis festhalten (sofort lokal, dann Warteschlange). Gleiche ID zweimal: nichts passiert.
  core.c1Record = e => {
    const L = core.local; if (!L.v2) throw new Error('kein Pilot gewählt');
    let ev;
    try { ev = L.v2.add(e); } catch (error) { L._v2ByPilot?.delete(L.state.pilot); core.rederive(); throw error; }
    if (ev) { if (config.learningFlow === FLOW && (['review','introduced'].includes(e.event_kind) || e.active_ms != null)) core.rederive(); else deriveC1Now(); emit('rated'); }
    return ev;
  };
  core.c1Preview = (T, skill, g, summary, t) => previewDue(T, skill, g, summary, t, sch(), core.policy()).due;   // derselbe Planer wie beim Speichern
  core.c1Context = sessionId => ({ deck: deckId, policy: core.pilot(), tz: core.tz, contentVersion: (content.meta && content.meta.source) || null, sessionId });
  core.learningEvents = () => config.learningFlow === FLOW ? PILOTS.flatMap(p => core.local.v2For(p).log) : core.local.v2?.log || [];
  core.studyDay = (t = core.now()) => studyDay(t, core.tz);
  core.noAudioToday = () => core.local.state.noAudioDay === core.studyDay();
  core.extraNewToday = () => { const x = core.local.state.extraNew; return x && x.day === core.studyDay() ? x.n : 0; };
  core.addExtraNew = n => { core.local.state.extraNew = { day: core.studyDay(), n: core.extraNewToday() + n }; core.local.save(); emit('changed'); };
  core.setNoAudioToday = on => { core.local.state.noAudioDay = on ? core.studyDay() : null; core.local.save(); emit('changed'); };
  core.extraMinutesToday = () => { const x = core.local.state.extraMinutes; return x?.day === core.studyDay() ? x.n : 0; };
  core.addExtraMinutes = n => { core.local.state.extraMinutes = { day: core.studyDay(), n: core.extraMinutesToday() + n }; core.local.save(); emit('changed'); };

  // Abgeleiteten Stand in core.S übernehmen
  function publish() {
    const L = core.local, S = core.S;
    S.units = withLocal(core.D.units, L.state.intro, L.state.flags);
    S.seen = core.D.seen; S.days = core.D.days; S.log = core.D.log;
    S.hidden = L.state.hidden; S.profile = L.state.profile; S.settings = L.user.settings; S.reports = L.state.reports;
    S.collection = L.state.collection; S.shiftDays = core.timeTravel ? L.state.shiftDays : 0;
  }
  core.rederive = () => { core.D = config.learningFlow === FLOW
    ? deriveSimple(core.local.events, core.learningEvents(), content, sch(), quietSet())
    : derive(core.local.events, sch(), quietSet()); deriveC1Now(); publish(); };

  core.useUser = userKey => {
    core.userKey = userKey;
    core.local = new LocalDeck({ store, userKey, deckId, deviceId: device });
    // Neue Einschreibung in den eingestellten Pilot; mit bisherigem Lernstand nur auf ausdrücklichen Wunsch (?pilot= in der Adresse)
    const L = core.local;
    if (config.learningFlow === FLOW && !L.state.pilot) { L.state.pilot = 'c1'; L.state.profileDirty = true; L.save(); }
    if (config.pilot && PILOTS.includes(config.pilot) && !L.state.pilot && (config.pilotExplicit || !L.events.length)) { L.state.pilot = config.pilot; L.state.profileDirty = true; L.save(); }
    core.rederive();
    emit('user');
  };

  // ---------- Aktionen der Oberfläche ----------
  core.preview = (u, g, t) => rateUnit(u, g, t, sch());
  core.rate = ({ unit_id, direction = 'r', sentence_id, rating, at, source = null }) => {
    const t = at || core.now(), quiet = !!source;
    const e = core.local.record({ unit_id, direction, sentence_id, rating, reviewed_at: t }, quiet);
    if (source === 'known') core.local.state.flags[unit_id] = { known: true };
    else if (source && source.startsWith('collection:')) core.local.state.flags[unit_id] = { known: false, fromCollection: source.slice(11) };
    if (source) core.local.save();
    core.D = append(core.D, e, core.local.events, sch(), quietSet());
    publish();
    emit('rated');
    return e;
  };
  core.introduce = (uid, t = core.now()) => { if (!(uid in core.local.state.intro)) { core.local.state.intro[uid] = t; core.local.save(); publish(); } };
  core.hide = (kind, id) => { core.local.hide(kind, id); publish(); emit('changed'); };
  core.restoreUnit = id => { core.local.unhide('unit', id); if (core.local.state.flags[id]) delete core.local.state.flags[id].known; core.local.save(); publish(); emit('changed'); };
  core.unhide = (sid, uid) => { core.local.unhide('sentence', sid); if (uid && core.local.state.hidden.u[uid]) core.local.unhide('unit', uid); publish(); emit('changed'); };
  core.report = (sid, reasons, text) => { core.local.report({ id: randomId(), sid, reasons, text, t: clock() }); publish(); emit('changed'); };
  core.startPos = () => startPosOf(core.local.state.profile);   // Startpunkt aus der Einstufung: davor gilt alles als vorausgesetzt
  core.checkFrom = () => checkFromOf(core.local.state.profile, content);   // davon geprüft: ab hier bis zum Startpunkt
  core.setProfile = patch => { Object.assign(core.local.state.profile, patch); core.local.state.profileDirty = true; core.local.save(); publish(); emit('changed'); };
  core.setSettings = patch => {
    const before = core.local.user.settings.retention;
    Object.assign(core.local.user.settings, patch); core.local.user.settingsDirty = true; core.local.saveUser();
    if (core.local.user.settings.retention !== before) core.rederive(); else publish();
    emit('changed');
  };
  core.saveCollection = () => core.local.save();      // die Oberfläche ändert S.collection[slug] direkt (wie im Prototyp)
  core.shift = days => { if (!core.timeTravel) return; core.local.state.shiftDays = days === 0 ? 0 : (core.local.state.shiftDays || 0) + days; core.local.save(); publish(); };

  // Lokale Sicherung als Datei: alle Bewertungen und lokalen Angaben dieses Decks
  core.exportLocal = () => JSON.stringify({ format: 'sentenzo-app-lokal-v1', exported_at: new Date().toISOString(), deck: deckId, user: core.userKey, device, events: core.local.events,
    learning_events_v2: core.learningEvents(), pilot: core.local.state.pilot, state: core.local.state, settings: core.local.user.settings }, null, 1);

  // ---------- Prototyp-Stand übernehmen ----------
  const mainOf = uid => { const u = content.units.find(x => x.id === uid); return u ? u.main : ''; };
  core.importPrototype = (P, targetDeck = deckId) => {
    const r = convertPrototype(P, targetDeck, clock(), targetDeck === deckId ? mainOf : () => '');
    const L = targetDeck === deckId ? core.local : new LocalDeck({ store, userKey: core.userKey, deckId: targetDeck, deviceId: device });
    let added = 0;
    for (const e of r.events) {
      if (L.engine.ids.has(e.id)) continue;
      L.engine.log.push(Object.assign({ device_id: device }, e)); L.engine.ids.add(e.id); L.engine.outbox.push(e.id); added++;
    }
    L.engine.persist();
    const st = L.state;
    for (const q of r.quiet) if (!st.quiet.includes(q)) st.quiet.push(q);
    for (const [k, v] of Object.entries(r.intro)) if (!(k in st.intro)) st.intro[k] = v;
    Object.assign(st.flags, r.flags);
    for (const kind of ['s', 'u']) for (const [id, t] of Object.entries(r.hidden[kind])) if (!(id in st.hidden[kind])) { st.hidden[kind][id] = t; st.hiddenOps.push({ op: 'add', kind: kind === 'u' ? 'unit' : 'sentence', id, t }); }
    const have = new Set(st.reports.map(x => x.id));
    for (const x of r.reports) if (!have.has(x.id)) { st.reports.push(x); st.reportOutbox.push(x.id); }
    for (const [slug, c] of Object.entries(r.collection)) if (!st.collection[slug]) st.collection[slug] = c;
    if (r.profile && r.profile.done && !st.profile.done) { st.profile = r.profile; st.profileDirty = true; }
    L.save();
    if (r.settings && !L.user.settingsDirty) { Object.assign(L.user.settings, r.settings); L.user.settingsDirty = true; L.saveUser(); }
    if (L === core.local) core.rederive();
    emit('changed');
    return Object.assign({ added, deck: targetDeck }, r.report);
  };

  // Einmalig beim Start: Prototyp-Stand aus localStorage (nur möglich, wenn die App unter derselben Adresse läuft wie der Prototyp)
  core.migratePrototypeOnce = () => {
    const results = [];
    for (const key of store.keys(PROTO_PREFIX)) {
      const target = protoKeyToDeck(key);
      if (!target || store.get(`sentenzo.app.migrated.${key}`)) continue;
      const P = readJson(store, key, null);
      if (!P) continue;
      try { results.push(Object.assign({ key }, core.importPrototype(P, target))); } catch (e) { results.push({ key, error: String(e.message || e) }); }
      store.set(`sentenzo.app.migrated.${key}`, JSON.stringify({ at: new Date().toISOString(), user: core.userKey }));   // Original bleibt unverändert liegen
    }
    return results;
  };

  core.adopt = (fromKey, toKey) => adoptUserData(store, fromKey, toKey, device);
  core.status = () => {
    const pending = core.local.pending();
    if (!core.account) return { mode: 'offline', state: 'offline', label: 'Offline-Modus', pending };
    const sy = core.sync ? core.sync.state : {};
    const base = { mode: 'online', pending, lastSyncAt: core.local.engine.lastSyncAt || null, user: core.account.user };
    if (!core.account.user) return Object.assign(base, { state: 'offline', label: 'nicht verbunden', detail: core.account.error || 'Anmeldung beim Server steht noch aus.' });
    if (sy.error) return Object.assign(base, { state: 'error', label: 'Fehler beim Sichern', error: sy.error });
    if (sy.offline) return Object.assign(base, { state: 'offline', label: pending ? 'offline, wird später gesichert' : 'offline' });
    return Object.assign(base, pending ? { state: 'pending', label: 'wird gesichert' } : { state: 'ok', label: 'gesichert' });
  };
  core.syncNow = async () => core.status();     // im Offline-Modus gibt es nichts zu senden (wird mit Konto ersetzt, siehe main.js)
  core.syncSoon = () => {};

  // ---------- Konto (Schritt 2) ----------
  // Kontowechsel: Daten eines anonymen Kontos (oder des Offline-Bereichs „lokal“) gehen nie verloren, sie werden ins neue Konto übernommen.
  core.userIsAnonymous = true;                 // gilt für den Bereich „lokal“
  core.onUser = (prev, next) => {
    if (!next) return;                         // Abmelden behandelt core.signOut
    if (next.id === core.userKey) { core.userIsAnonymous = next.is_anonymous; emit('status'); return; }   // z. B. Konto gesichert: gleiche ID
    const from = core.userKey;
    if (from === LOCAL_USER || core.userIsAnonymous) {
      const moved = core.adopt(from, next.id);
      if (moved) console.info(`${moved} Bewertungen von ${from === LOCAL_USER ? 'diesem Gerät' : 'der Anmeldung ohne Konto'} ins Konto übernommen`);
    }
    // Rückkehr von Google: Die Seite wurde neu geladen, der Lernstand liegt noch unter der anonymen Kennung von vor der Weiterleitung
    const before = core.account && core.account.adoptFrom;
    if (before) {
      core.account.adoptFrom = null;
      if (before !== next.id && !next.is_anonymous) core.adopt(before, next.id);
    }
    core.userIsAnonymous = next.is_anonymous;
    core.useUser(next.id);
  };
  // Noch nicht hochgeladene Einträge eines Kontos auf diesem Gerät (alle Decks)
  core.pendingAll = (userKey = core.userKey) => {
    let n = 0;
    for (const d of decksOf(store, userKey)) { const L = d === deckId && userKey === core.userKey ? core.local : new LocalDeck({ store, userKey, deckId: d, deviceId: device }); n += L.pending() - (L.user.settingsDirty ? 1 : 0); }
    const u = readJson(store, `sentenzo.app.${userKey}.user`, {});
    return n + (u.settingsDirty ? 1 : 0);
  };
  core.attachAccount = account => {
    core.account = account;
    // Sitzung von außen beendet (abgelaufen, Konto gelöscht, in anderem Tab abgemeldet): gleich wieder anonym anmelden. Der bisherige Bereich bleibt
    // aktiv, bis das neue Konto da ist; war er anonym, wird er übernommen, sonst bleiben seine Daten für die nächste Anmeldung liegen.
    account.onChange = (event, prev, next) => {
      if (next) return core.onUser(prev, next);
      emit('status');
      if (!core._signingOut) setTimeout(() => account.ensureUser(), 0);
    };
    // Fassade für die Oberfläche: Konto-Aktionen plus lokale Regeln (nichts Ungesichertes beim Abmelden verlieren)
    core.accountApi = {
      get user() { return account.user; },
      get error() { return account.error; },
      takeUrlError() { const e = account.urlError; account.urlError = null; return e; },   // Fehler beim Rücksprung aus einer Mail, einmal anzeigen
      secure: email => account.secure(email),
      signIn: email => account.signIn(email),
      confirm: (email, code, type) => account.confirm(email, code, type),
      oauth: (provider, opts) => account.oauth(provider, opts),
      get providers() { return core.config.authProviders || []; },
      exportData: () => account.exportData(),
      async signOut({ force = false } = {}) {
        if (!account.user) return { ok: false, message: 'Du bist nicht angemeldet.' };
        await core.syncNow();
        const key = core.userKey, pending = core.pendingAll(key);
        if (pending && !force) return { blocked: pending };
        core._signingOut = true;
        let r;
        try { r = await account.signOut(); } finally { core._signingOut = false; }
        if (!r.ok) return r;
        // Gesicherte Daten vom Gerät entfernen (sie stehen im Konto). Ungesicherte bleiben liegen und gehen bei der nächsten Anmeldung mit diesem Konto hoch.
        if (!pending) removeUserData(store, key);
        core.userIsAnonymous = true;
        core.useUser(LOCAL_USER);
        account.ensureUser();                    // gleich wieder ein anonymes Konto, damit weiter gesichert wird
        return { ok: true };
      },
      // Konto endgültig löschen: auf dem Server (alle Zeilen hängen am Konto) und auf diesem Gerät, auch noch nicht Hochgeladenes.
      // Danach beginnt ein neues anonymes Konto bei null.
      async deleteAccount() {
        if (!account.user) return { ok: false, message: 'Du bist nicht angemeldet.' };
        const key = core.userKey;
        core._signingOut = true;
        let r;
        try { r = await account.deleteAccount(); } finally { core._signingOut = false; }
        if (!r.ok) return r;
        removeUserData(store, key);
        core.userIsAnonymous = true;
        core.useUser(LOCAL_USER);
        account.ensureUser();
        return { ok: true };
      },
    };
    return core.accountApi;
  };

  // ---------- Synchronisierung (Schritt 3) ----------
  core.attachSync = (client, opts = {}) => {
    core.sync = new SyncService(core, client, opts);
    core.syncNow = () => core.sync.syncNow();
    core.syncSoon = () => core.sync.soon();
    core.on(what => { if (what === 'rated' || what === 'changed') core.sync.soon(); else if (what === 'user' || what === 'session-end') core.sync.syncNow(); });
    return core.sync;
  };

  core.useUser(LOCAL_USER);
  return core;
}
